import { useMemo, useState } from "react";

import { DEFAULT_GANTT_INDICATORS, GANTT_INDICATOR_OPTIONS } from "@/modules/carga-trabalho";
import type { GanttIndicators } from "@/modules/carga-trabalho";
import { useCalendarioAlocacao } from "./hooks/use-calendario-alocacao";
import { buildCalendarioRows } from "./calendario-alocacao-tab.utils";
import type { CalendarioAlocacaoTabProps } from "./types-calendario-alocacao-tab";
import { unirIndisponibilidades } from "@/modules/recursos";

export function useCalendarioAlocacaoTab({
  obra,
  novas,
  profissionais,
  today,
}: CalendarioAlocacaoTabProps) {
  const [indicators, setIndicators] = useState<GanttIndicators>(DEFAULT_GANTT_INDICATORS);
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);

  const membros = useMemo(() => {
    const ids = [...new Set(novas.map((nova) => nova.usuarioId))];
    return ids.map((id) => {
      const profissional = profissionais.find((item) => item.id === id);
      return {
        id,
        nome: profissional?.nome ?? "",
        time: profissional?.cargo || profissional?.time || null,
      };
    });
  }, [novas, profissionais]);

  const calendario = useCalendarioAlocacao({
    periodo: { inicio: obra.inicio, fim: obra.fim },
    profissionalIds: membros.map((membro) => membro.id),
  });

  const gantt = useMemo(() => {
    if (calendario.status !== "success") return null;
    return buildCalendarioRows({
      obra,
      membros,
      novas,
      carga: calendario.profissionais,
      indisponibilidades: unirIndisponibilidades(
        membros.map((membro) => membro.id),
        calendario.profissionais,
        profissionais,
      ),
      indicators,
      today,
    });
  }, [calendario, obra, membros, novas, profissionais, indicators, today]);

  const semMembros = membros.length === 0;

  return {
    semMembros,
    carregando: !semMembros && calendario.status === "loading",
    falhou: !semMembros && (calendario.status === "error" || calendario.status === "forbidden"),
    podeTentarDeNovo: calendario.status === "error",
    mensagemErro: calendario.errorMessage,
    tentarDeNovo: calendario.reload,
    gantt,
    indicators,
    indicatorOptions: GANTT_INDICATOR_OPTIONS,
    indicatorsOpen,
    abrirIndicadores: () => setIndicatorsOpen(true),
    fecharIndicadores: () => setIndicatorsOpen(false),
    aplicarIndicadores: (proximos: GanttIndicators) => {
      setIndicators(proximos);
      setIndicatorsOpen(false);
    },
  };
}
